<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  activeList: { type: String, required: true },
  item: { type: Object, required: true },
  visible: { type: Boolean, default: true },
});

const emit = defineEmits(['action']);
const { t } = useI18n();
const open = ref(false);
const menu = ref(null);

const can = permission => Boolean(props.item.permissions?.[permission]);
const canCancel = computed(
  () =>
    can('can_cancel') &&
    !['finalizado', 'cancelado'].includes(props.item.status)
);

const menuItems = computed(() => {
  if (props.activeList !== 'andamento') return [];

  const items = [];
  if (can('can_manage')) {
    items.push({
      action: 'status',
      value: 'status',
      label: t('POST_SALE.TABLE_ACTIONS.CHANGE_STATUS'),
      icon: 'i-lucide-arrow-right-left',
    });
  }
  if (can('can_request_correction')) {
    items.push({
      action: 'review',
      value: 'review',
      label: t('POST_SALE.TABLE_ACTIONS.REQUEST_REVIEW'),
      icon: 'i-lucide-undo-2',
    });
  }
  if (can('can_request_nucleo')) {
    items.push({
      action: 'nucleo',
      value: 'nucleo',
      label: t('POST_SALE.TABLE_ACTIONS.REQUEST_SERVICE'),
      icon: 'i-lucide-file-plus-2',
    });
  }
  if (can('can_assign')) {
    items.push({
      action: 'assign',
      value: 'assign',
      label: t('POST_SALE.TABLE_ACTIONS.CHANGE_RESPONSIBLE'),
      icon: 'i-lucide-user-round-cog',
    });
  }
  items.push({
    action: 'details',
    value: 'details',
    label: t('POST_SALE.TABLE_ACTIONS.VIEW_DETAILS'),
    icon: 'i-lucide-info',
  });
  if (canCancel.value) {
    items.push({
      action: 'delete',
      value: 'cancel',
      label: t('POST_SALE.TABLE_ACTIONS.CANCEL_SALE'),
      icon: 'i-lucide-trash-2',
    });
  }
  return items;
});

const menuSections = computed(() => [
  {
    title: t('POST_SALE.TABLE_ACTIONS.OPTIONS_TITLE'),
    items: menuItems.value,
  },
]);

const simpleAction = computed(() =>
  props.activeList === 'revisoes'
    ? {
        action: 'review',
        label: t('POST_SALE.TABLE_ACTIONS.VIEW_REVIEW'),
      }
    : {
        action: 'details',
        label: t('POST_SALE.TABLE_ACTIONS.VIEW_DETAILS'),
      }
);
const menuId = computed(() => `post-sale-table-actions-${props.item.id}`);

const close = () => {
  menu.value?.hidePopover?.();
  open.value = false;
};

watch(
  () => props.visible,
  visible => {
    if (!visible) close();
  }
);

const select = item => {
  close();
  emit('action', { action: item.value, postSaleCase: props.item });
};

const syncPopover = event => {
  open.value = event.newState === 'open';
};
</script>

<template>
  <td class="p-3 text-right">
    <template v-if="visible">
      <div v-if="activeList === 'andamento'" class="inline-flex">
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-xs font-semibold text-white transition hover:bg-lais-primary/90 focus-visible:outline-none focus-visible:shadow-lais-focus"
          :aria-label="t('POST_SALE.TABLE_ACTIONS.OPTIONS')"
          aria-haspopup="menu"
          :aria-expanded="open ? 'true' : 'false'"
          :popovertarget="menuId"
          data-test="post-sale-table-actions-trigger"
        >
          <span class="i-lucide-ellipsis size-4" aria-hidden="true" />
          {{ t('POST_SALE.TABLE_ACTIONS.OPTIONS') }}
        </button>
        <div
          :id="menuId"
          ref="menu"
          popover="auto"
          class="inset-auto m-0 border-0 bg-transparent p-0 [position-area:block-start_span-inline-end]"
          @toggle="syncPopover"
        >
          <DropdownMenu
            :menu-sections="menuSections"
            class="!static w-56"
            data-test="post-sale-table-actions-menu"
            @click.stop
            @action="select"
          />
        </div>
      </div>
      <button
        v-else
        type="button"
        class="inline-flex items-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-xs font-semibold text-white transition hover:bg-lais-primary/90 focus-visible:outline-none focus-visible:shadow-lais-focus"
        :aria-label="simpleAction.label"
        @click.stop="
          emit('action', {
            action: simpleAction.action,
            postSaleCase: item,
          })
        "
      >
        <span class="i-lucide-info size-4" aria-hidden="true" />
        {{ simpleAction.label }}
      </button>
    </template>
    <span v-else class="block size-9" aria-hidden="true" />
  </td>
</template>
